import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Wallet, TrendingUp, HandCoins, PiggyBank, Truck, MessageCircle, ChefHat, AlertTriangle, ArrowRight } from "lucide-react";
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid } from "recharts";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Progress } from "@/components/ui/progress";
import { PageHeader, StatCard, Money } from "@/components/common";
import { CollectionLeaderboard } from "@/components/CollectionLeaderboard";
import { RatingsCard } from "@/components/RatingsCard";
import { api, errMsg } from "@/lib/api";
import { inr0, fmtDate } from "@/lib/format";

function TomorrowCard({ t, date }) {
  return (
    <div className="relative overflow-hidden rounded-3xl bg-[#0F382C] p-6 sm:p-8 text-white shadow-xl lg:col-span-2 bf-rise" data-testid="tomorrow-card">
      <div className="absolute inset-0 bf-grain opacity-60" />
      <div className="relative">
        <div className="text-xs uppercase tracking-[0.25em] text-[#F2B880]">Tomorrow · {fmtDate(date)}</div>
        <div className="font-display text-2xl sm:text-3xl font-bold mt-2">Customer confirmations</div>
        <div className="grid grid-cols-3 gap-3 mt-6">
          {[["Confirmed", t.confirmed, "text-emerald-300"], ["Pending", t.pending, "text-amber-300"], ["No need", t.skipped, "text-slate-300"]].map(([l, v, c]) => (
            <div key={l} className="rounded-2xl bg-white/10 p-4 backdrop-blur-md">
              <div className={`font-mono text-3xl font-semibold ${c}`} data-testid={`tomorrow-${l.toLowerCase().replace(" ", "-")}`}>{v}</div>
              <div className="text-xs text-emerald-100/80 mt-1">{l}</div>
            </div>
          ))}
        </div>
        <div className="mt-6 flex flex-wrap gap-2">
          {t.products.length === 0 && <span className="text-sm text-emerald-100/70">No confirmed quantities yet.</span>}
          {t.products.map((p) => (
            <span key={p.name} className="rounded-full bg-[#FBF9F4] px-3 py-1 text-sm font-semibold text-[#0F382C]">{p.name}: <span className="font-mono">{p.qty}</span></span>
          ))}
        </div>
        <div className="mt-6 flex flex-wrap gap-2">
          <Button asChild className="rounded-full bg-[#25D366] hover:bg-[#20bd5a] text-white" data-testid="dash-open-confirmations">
            <Link to="/confirmations"><MessageCircle className="mr-2 h-4 w-4" />Ask customers on WhatsApp</Link>
          </Button>
          <Button asChild variant="outline" className="rounded-full border-white/30 bg-transparent text-white hover:bg-white/10 hover:text-white" data-testid="dash-open-production">
            <Link to="/production"><ChefHat className="mr-2 h-4 w-4" />Production plan</Link>
          </Button>
        </div>
      </div>
    </div>
  );
}

function DashAlerts() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/alerts").then((r) => setItems(r.data.items.filter((a) => !a.read).slice(0, 4))).catch(() => {}); }, []);
  if (!items.length) return null;
  return (
    <div className="rounded-2xl border border-rose-300 border-l-4 border-l-rose-500 bg-white p-5" data-testid="dash-alerts-card">
      <div className="flex items-center gap-2 font-display font-semibold text-rose-700"><AlertTriangle className="h-4 w-4" />Alerts: credit & batch expiry</div>
      <div className="mt-2 space-y-2 text-sm">{items.map((a) => <Link key={a.id} to={`/customers/${a.customer_id}`} className="block hover:underline">{a.message}</Link>)}</div>
    </div>
  );
}

export default function Dashboard() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/dashboard").then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e))); }, []);
  if (!d) return <div className="h-64 animate-pulse rounded-3xl bg-white" data-testid="dashboard-loading" />;
  const td = d.today_deliveries;
  return (
    <div data-testid="dashboard-page">
      <PageHeader eyebrow={fmtDate(d.today)} title="Today at the kitchen" subtitle="Everything you need to plan tomorrow's grind and collect this week's money." />
      <div className="grid grid-cols-1 gap-5 lg:grid-cols-4">
        <TomorrowCard t={d.tomorrow_confirmations} date={d.tomorrow} />
        <StatCard tone="amber" label="Outstanding credit" value={inr0(d.total_outstanding)} icon={Wallet} hint="Total to collect from customers" testid="stat-outstanding" />
        <div className="rounded-2xl border border-[#E7E2D6] bg-white p-5 shadow-sm bf-rise bf-d1" data-testid="today-deliveries-card">
          <div className="flex items-start justify-between"><div className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">Today's deliveries</div><Truck className="h-5 w-5 text-[#0F382C]/60" /></div>
          <div className="font-mono text-3xl font-semibold mt-3">{td.delivered}<span className="text-lg text-muted-foreground">/{td.total}</span></div>
          <Progress value={td.total ? (td.delivered / td.total) * 100 : 0} className="mt-3 h-2" />
          <div className="text-xs text-muted-foreground mt-2">{td.customer_confirmed} confirmed by customer · {td.failed} failed</div>
          <Link to="/deliveries" className="mt-3 inline-flex items-center text-sm font-semibold text-[#0F382C] hover:underline" data-testid="dash-open-deliveries">Open deliveries <ArrowRight className="ml-1 h-3 w-3" /></Link>
        </div>
        <StatCard label="Sales · 7 days" value={inr0(d.week_sales)} icon={TrendingUp} testid="stat-week-sales" />
        <StatCard label="Collected · 7 days" value={inr0(d.week_collections)} icon={HandCoins} tone="green" testid="stat-week-collections" />
        <StatCard label="Production cost · 7 days" value={inr0(d.week_production_cost)} icon={ChefHat} hint={`Market purchases: ${inr0(d.week_purchases)}`} testid="stat-week-cost" />
        <StatCard tone="dark" label="Gross profit · 7 days" value={inr0(d.week_gross_profit)} icon={PiggyBank} hint="Sales minus raw-material cost" testid="stat-week-profit" />

        <div className="rounded-2xl border border-[#E7E2D6] bg-white p-5 shadow-sm lg:col-span-3" data-testid="trend-chart">
          <div className="font-display text-lg font-semibold text-[#0F382C]">Sales vs collections · 14 days</div>
          <div className="h-64 mt-4">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart data={d.trend.map((t) => ({ ...t, label: t.date.slice(5) }))}>
                <CartesianGrid strokeDasharray="3 3" stroke="#EFEBE1" />
                <XAxis dataKey="label" fontSize={11} tickLine={false} axisLine={false} />
                <YAxis fontSize={11} tickLine={false} axisLine={false} width={50} />
                <Tooltip formatter={(v) => inr0(v)} />
                <Area type="monotone" dataKey="sales" stroke="#0F382C" fill="#0F382C" fillOpacity={0.15} strokeWidth={2} name="Sales" />
                <Area type="monotone" dataKey="collections" stroke="#D96B43" fill="#D96B43" fillOpacity={0.12} strokeWidth={2} name="Collections" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>
        <div className="space-y-5">
          <DashAlerts />
          <div className="rounded-2xl border border-[#E7E2D6] bg-white p-5 shadow-sm" data-testid="top-dues-card">
            <div className="font-display font-semibold text-[#0F382C]">Top dues</div>
            <div className="mt-3 space-y-2">
              {d.top_due.length === 0 && <div className="text-sm text-muted-foreground">No dues. Excellent!</div>}
              {d.top_due.map((c) => (
                <Link key={c.customer_id} to={`/customers/${c.customer_id}`} className="flex justify-between text-sm hover:underline">
                  <span className="truncate">{c.name}</span><Money value={c.balance} className="text-amber-700 font-semibold" />
                </Link>
              ))}
            </div>
          </div>
          <div className="rounded-2xl border border-rose-200 bg-rose-50/60 p-5" data-testid="low-stock-card">
            <div className="flex items-center gap-2 font-display font-semibold text-rose-700"><AlertTriangle className="h-4 w-4" />Low stock</div>
            <div className="mt-3 space-y-1 text-sm">
              {d.low_stock.length === 0 && <div className="text-muted-foreground">All raw materials stocked.</div>}
              {d.low_stock.map((m) => <div key={m.id} className="flex justify-between"><span>{m.name}</span><span className="font-mono">{Number(m.stock).toFixed(2)} {m.unit}</span></div>)}
            </div>
          </div>
        </div>
        <CollectionLeaderboard data={d.leaderboard} />
        <RatingsCard />
      </div>
    </div>
  );
}
